/**
 * useNotificationPreferences — React Query hooks for the email-notification
 * settings tab on the profile page.
 *
 * Endpoints under /api/notification-prefs:
 *   GET    /me                — Catalog joined with this user's effective prefs
 *   PUT    /me                — Bulk-update one or more prefs
 *   POST   /me/select-all     — Turn all non-required types ON
 *   POST   /me/deselect-all   — Turn all non-required types OFF
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Types ---

export interface NotificationTypeWithPreference {
  key: string;
  label: string;
  description: string | null;
  category: string;
  default_enabled: boolean;
  is_required: boolean;
  active: boolean;
  sort_order: number;
  /** Effective state for this user: explicit pref OR catalog default. */
  enabled: boolean;
  /** Whether the user has saved an explicit override. */
  is_user_set: boolean;
}

export interface UpdatePreferenceItem {
  key: string;
  enabled: boolean;
}

// --- Query keys ---

export const notificationPrefsKeys = {
  all: ['notification-prefs'] as const,
  me: () => [...notificationPrefsKeys.all, 'me'] as const,
};

// --- Query hook ---

/**
 * Load every active notification type joined with the user's effective
 * preference (explicit override or catalog default). Returns rows already
 * sorted by category + sort_order.
 *
 * The hook is gated by `enabled` so the settings tab can defer the request
 * until the route is actually mounted — no point spending a roundtrip on
 * users who never visit it.
 */
export function useMyNotificationPreferences(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: notificationPrefsKeys.me(),
    queryFn: async () => {
      const res = await httpClient.get<{
        data: NotificationTypeWithPreference[];
        success: boolean;
      }>('/notification-prefs/me');
      return res.data;
    },
    enabled: opts?.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

// --- Mutation hooks ---

/**
 * Bulk-update one or more preferences. The backend silently drops
 * required-type updates, so the UI doesn't need to defensively filter
 * those before calling this hook — but it's still cleaner to omit them.
 */
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (preferences: UpdatePreferenceItem[]) => {
      const res = await httpClient.put<{
        data: NotificationTypeWithPreference[];
        success: boolean;
      }>('/notification-prefs/me', { preferences });
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(notificationPrefsKeys.me(), data);
    },
  });
}

/** Turn every non-required notification type ON for the current user. */
export function useSelectAllNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await httpClient.post<{
        data: NotificationTypeWithPreference[];
        success: boolean;
      }>('/notification-prefs/me/select-all', {});
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(notificationPrefsKeys.me(), data);
    },
  });
}

/** Turn every non-required notification type OFF for the current user. */
export function useDeselectAllNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await httpClient.post<{
        data: NotificationTypeWithPreference[];
        success: boolean;
      }>('/notification-prefs/me/deselect-all', {});
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(notificationPrefsKeys.me(), data);
    },
  });
}

// --- View helpers ---

/**
 * Group preferences by category, preserving the catalog's sort_order. The
 * settings tab renders one section per group.
 */
export function groupByCategory(
  rows: NotificationTypeWithPreference[],
): Array<{ category: string; items: NotificationTypeWithPreference[] }> {
  const map = new Map<string, NotificationTypeWithPreference[]>();
  for (const row of rows) {
    const list = map.get(row.category) ?? [];
    list.push(row);
    map.set(row.category, list);
  }
  return Array.from(map.entries()).map(([category, items]) => ({
    category,
    items,
  }));
}

/**
 * Human-readable label for a category key. Falls back to a title-cased
 * version of the key if we don't have a hand-tuned label, so the UI
 * doesn't blow up when a new category is added to the catalog before
 * the frontend learns its display name.
 */
export function categoryLabel(category: string): string {
  switch (category) {
    case 'buddy_groups':
      return 'Buddy groups';
    case 'partners':
      return 'Partners';
    case 'registrations':
      return 'Registrations';
    case 'payments':
      return 'Payments & funds';
    case 'account':
      return 'Account & security';
    default:
      return category
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}
